import { useEffect, useRef, useState } from "react";
import { IJSTreeNode } from "../../../models/Interfaces";
import { ContentService } from "../../../services/ContentService";
import InputGroupText from "react-bootstrap/esm/InputGroupText";
import { Alert, Col, FormControl, FormLabel, InputGroup, Row } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faRefresh, faTimes } from "@fortawesome/free-solid-svg-icons";
import TreeSelect from "./TreeSelect";
import { TreeItemSort } from "../../../config/Enums";

interface IPageSelector {
  name?: string;
  id?: string;
  existingUrl?: string;
  selectedPageId?: number;
  onPageSelect?: (selectedurl: string) => void;
  onPageClear?: () => void;
  onInputSelect?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  allowInput?: boolean;
  closeOnSelect?: boolean;
}

const PageSelector = (props: IPageSelector) => {
  const [ pages, setPages ] = useState<Array<IJSTreeNode>>([]);
  const serviceRef = useRef<ContentService>();
  const [selectedUrl, setSelectedUrl] = useState<string>(props.existingUrl ?? "");
  const [refreshingPages, setRefreshingPages] = useState<boolean>(false);

  const getPages = async () => {
    if (serviceRef.current) {
      setRefreshingPages(true);
      const res = await serviceRef.current.getPagesTreeData();

      if (res?.length > 0) {
        setPages(res);
      }
      setRefreshingPages(false);
    }
  };

  const onPageSelect = (value: string) => {
    setSelectedUrl(value);
    if (props.onPageSelect) {
      props.onPageSelect(value);
    }

    if (props.onInputSelect) {
      const name = props.name ?? "PageSelect"
      const evt = { 
        target: { 
          value: value, 
          name: name 
        },
        preventDefault: () => {},
        stopPropagation: () => {},
      };
      props.onInputSelect(evt as unknown as React.ChangeEvent<HTMLInputElement>);
    }
  };

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    
    setSelectedUrl(event.target.value);

    const value = event.target.value;
    if (props.onPageSelect) {
      props.onPageSelect(value);
    }

    if (props.onInputSelect) {
      props.onInputSelect(event);
    }
  };

  const clearSelection = (): void => {
    setSelectedUrl("");

    if (props.onPageClear) {
      props.onPageClear();
    }

    if (props.onPageSelect) {
      props.onPageSelect("");
    }

    if (props.onInputSelect) {
      const name = props.name ?? "PageSelect";
      const evt = {
        target: {
          value: "",
          name
        },
        preventDefault: () => {},
        stopPropagation: () => {},
      };
      props.onInputSelect(evt as unknown as React.ChangeEvent<HTMLInputElement>);
    }
  };

  const setSelectedFromPageId = (pageid: number) : void => {
    if (pageid && pages) {
      const page = pages.find(p => p.id === pageid);

      if (page) {
        setSelectedUrl(page.url);
      }
    }
  };

  useEffect(() => {
    if (props.selectedPageId) {
      setSelectedFromPageId(props.selectedPageId);
    }
  }, [props.selectedPageId])

  useEffect(() => {
    if (!serviceRef.current) {
      serviceRef.current = new ContentService();
    }

    const getData = async () => {
      try {
        await getPages();

        if (props.selectedPageId) {
          setSelectedFromPageId(props.selectedPageId);
        }

      } catch (error) {
        console.log(error);
      }
    }
    getData();
  }, []);

  return (
    <>
      <Row>
        <Col md={12}>
          <FormControl
            id={props.id ?? "SelectedUrl"}
            name={props.name ?? "SelectedUrl"}
            type="text"
            placeholder="selected or entered url..."
            value={selectedUrl}
            onChange={handleInputChange}
            readOnly={props.allowInput !== undefined && props.allowInput === false ? true : undefined}
          />
        </Col>
      </Row>

      { pages.length > 0 ? (
        <>
          <Row>
            <Col md={12} className="mt-3">
              <InputGroup>
                <TreeSelect
                  typeKey="type"
                  valueKey="url"
                  displayKey="text"
                  items={pages}
                  parentKey="parent"
                  idKey="id"
                  closeText="close pages list"
                  openText="select site page..."
                  onItemSelect={onPageSelect}
                  selectnodetype="page"
                  initialValue={selectedUrl}
                  style={{ flexGrow: 1 }}
                  rootNodeKeyValue={"#"} 
                  closeOnSelect={props.closeOnSelect !== undefined ? props.closeOnSelect : false} 
                  popoverTitle="Select Page"
                  hideClear={true}
                  triggerClassName="page-selector__trigger"
                  sortBy={TreeItemSort.Unsorted}/>
                <InputGroupText className="page-selector__action" title="clear selected page" onClick={clearSelection} style={{ cursor: "pointer" }}>
                  <FontAwesomeIcon icon={faTimes} />
                </InputGroupText>
                <InputGroupText className="page-selector__action page-selector__action--last">
                  <FontAwesomeIcon icon={faRefresh} spin={refreshingPages} style={{cursor: "pointer"}} title="refresh page list..." onClick={getPages} />
                </InputGroupText>
              </InputGroup>
            </Col>
          </Row>
        </>
      ) : (
        <div>
          <Alert variant="info" className="text-center fw-bold">
            <span>No pages to select</span>
          </Alert>
        </div>
      )}
    </>
  );
}

export default PageSelector;
